import { trimTrailingDecimalZeroes } from "./add-decimal-point";
import { numToStr, unknownToStr } from "./type-safe-stringify";
// Format: `< ${sign}0.${zeros}1` (positive) or `> ${sign}0.${zeros}1` (negative)
// Note: The dot (.) and space ( ) are included in the output but NOT counted in length constraint
// Fixed parts counted: `<` or `>` (1 char, space not counted) + `0` (1 char) = 2 chars
// Variable parts counted: sign.length + zeros.length + `1` (1 char)
const FIXED_PARTS_LENGTH = 3; // "<" or ">" (space not counted) + "0" (dot not counted) + "1" (1 char in the tail)

const ONE_K_DIGITS = 4; // Minimum length required for formatToFixedLength
const ONE_K_DIGITS_STR = numToStr(ONE_K_DIGITS);

export function formatToFixedLength(
	numWithTrailingZeroes: string,
	length: number,
): string {
	if (length < ONE_K_DIGITS) {
		throw new Error(
			`formatToFixedLength: length must be at least ${ONE_K_DIGITS_STR}, got ${numToStr(length)}`,
		);
	}

	if (numWithTrailingZeroes === "0" || numWithTrailingZeroes === "0.0") {
		return "0";
	}

	const num = trimTrailingDecimalZeroes(numWithTrailingZeroes);
	const sign = num.startsWith("-") ? "-" : "";
	const absNum = sign === "-" ? num.slice(1) : num;
	const [integerPart = "0", decimalPart = ""] = absNum.split(".");
	const digitsWithoutDecimal = integerPart + decimalPart;

	if (digitsWithoutDecimal.length <= length) {
		return num;
	}

	if (integerPart.length >= length) {
		return formatWithSuffix(integerPart, decimalPart, sign, length);
	}

	const remainingDigits = length - integerPart.length;
	const truncatedDecimal = decimalPart.slice(0, remainingDigits);
	const integerPartIsAllZeros = integerPart === "0".repeat(integerPart.length);

	const truncatedDecimalIsAllZeros =
		truncatedDecimal === "0".repeat(truncatedDecimal.length);

	const zerosLength = Math.max(0, length - sign.length - FIXED_PARTS_LENGTH);
	const comparisonSymbol = sign === "-" ? ">" : "<";

	return integerPartIsAllZeros && truncatedDecimalIsAllZeros
		? `${comparisonSymbol} ${sign}0.${"0".repeat(zerosLength)}1`
		: decimalPart === "0".repeat(decimalPart.length)
			? `${sign}${integerPart}...`
			: `${sign}${integerPart}.${truncatedDecimal}`;
}

const suffixes = [
	{ digits: 16, suffix: "Q", divisor: 15 }, // Quadrillion
	{ digits: 13, suffix: "T", divisor: 12 }, // Trillion
	{ digits: 10, suffix: "B", divisor: 9 }, // Billion
	{ digits: 7, suffix: "M", divisor: 6 }, // Million
	{ digits: ONE_K_DIGITS, suffix: "K", divisor: ONE_K_DIGITS - 1 }, // Thousand
];

function formatWithSuffix(
	integerPart: string,
	decimalPart: string,
	sign: string,
	length: number,
): string {
	const integerDigits = integerPart.length;

	// Find the appropriate suffix based on number of digits
	for (const { digits, suffix, divisor } of suffixes) {
		if (integerDigits < digits) {
			continue;
		}

		// Divide by moving decimal point divisor places to the left
		const abbreviated = divideByPowerOfTen(integerPart, decimalPart, divisor);
		const [abbrInt = "0", abbrDec = ""] = abbreviated.split(".");

		// Reserve 1 character for the suffix, so available length is length - 1
		const availableLength = length - 1;

		if (abbrInt.length >= availableLength) {
			// For Q suffix (Quadrillion), ignore length constraint and show full integer part
			if (suffix === "Q") {
				return `${sign}${abbrInt}${suffix}`;
			}

			// For other suffixes, truncate
			const truncated = abbrInt.slice(0, availableLength);
			return `${sign}${truncated}${suffix}`;
		}

		// Fit as much as possible: integer part + decimal part + suffix
		const remainingDigits = availableLength - abbrInt.length;

		if (remainingDigits > 0 && abbrDec.length > 0) {
			const truncatedDecimal = abbrDec.slice(0, remainingDigits);
			return `${sign}${abbrInt}.${truncatedDecimal}${suffix}`;
		}

		return `${sign}${abbrInt}${suffix}`;
	}

	// Fallback: this should be unreachable with ONE_K_DIGITS >= 4
	// If length >= 4 and integerPart.length >= length, then integerDigits >= 4
	// Since the smallest suffix requires 4 digits (K), it will always match
	throw new Error(
		`formatWithSuffix fallback reached unexpectedly. This should be unreachable with ONE_K_DIGITS >= 4. integerDigits: ${numToStr(integerDigits)}, length: ${numToStr(length)}`,
	);
}
/**
 * Divides a number string by 10^power by moving the decimal point.
 * Handles very large numbers without precision loss.
 */

function divideByPowerOfTen(
	integerPart: string,
	decimalPart: string,
	power: number,
): string {
	const allDigits = integerPart + decimalPart;
	const totalDigits = allDigits.length;

	if (power >= totalDigits) {
		// Result would be less than 1
		const leadingZeros = power - totalDigits;
		const significantDigits = allDigits.replace(LEADING_ZEROS_REGEX, "");

		if (significantDigits === "0") {
			return "0";
		}

		const result = `0.${"0".repeat(leadingZeros)}${significantDigits}`;
		return trimTrailingDecimalZeroes(result);
	}

	// Move decimal point power places to the left
	const newDecimalPoint = integerPart.length - power;

	if (newDecimalPoint <= 0) {
		// Decimal point moves into integer part
		const leadingZeros = -newDecimalPoint;
		const result = `0.${"0".repeat(leadingZeros)}${allDigits}`;
		return trimTrailingDecimalZeroes(result);
	}

	// Decimal point stays in valid position
	const newIntegerPart = allDigits.slice(0, newDecimalPoint);
	const newDecimalPart = allDigits.slice(newDecimalPoint);

	const result =
		newDecimalPart !== ""
			? `${newIntegerPart}.${newDecimalPart}`
			: newIntegerPart;

	return trimTrailingDecimalZeroes(result);
}

const LEADING_ZEROS_REGEX = /^0+/;

export function trimToDecimals(num: string, decimals: number) {
	if (!num.includes(".")) {
		return num;
	}

	const [integerPart, decimalPart] = num.split(".");

	if (integerPart === undefined || decimalPart === undefined) {
		throw new Error(
			`Unreachable: trimToDecimals: invalid number format: ${num}. integerPart: ${unknownToStr(integerPart)}, decimalPart: ${unknownToStr(decimalPart)}`,
		);
	}

	const trimmedDecimalPart = decimalPart.slice(0, decimals);

	// If decimals is 0 or all trimmed decimals are empty, return integer part only
	if (decimals === 0 || trimmedDecimalPart === "") {
		return integerPart;
	}

	// Remove trailing zeros and decimal point if no decimals remain
	return trimTrailingDecimalZeroes(`${integerPart}.${trimmedDecimalPart}`);
}
